import React, { useState, useEffect } from "react";
import { Layout, Row, Col, Typography, Button, Space, Switch, Input, Divider, message } from "antd";
import {
  UnorderedListOutlined,
  ToolOutlined,
  CarOutlined,
  TagOutlined,
  SearchOutlined,
  ReadOutlined,
  BulbOutlined,
  BulbFilled,
  StarFilled,
  LinkedinFilled,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import "./home.scss";
import TopBar from "../features/TopBar/TopBar";
import logo from "../assets/logo.png";

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;

// --- Data ---
const stories = [
  {
    quote: "I discovered a transmission issue before signing the papers. Saved me thousands.",
    name: "James Mitchell",
    role: "Used Audi Buyer",
    initial: "J"
  },
  {
    quote: "The maintenance gaps were obvious once I saw the full history. Knew exactly what to negotiate.",
    name: "Sarah Chen",
    role: "First-time Buyer",
    initial: "S"
  },
  {
    quote: "Their market analysis showed the asking price was inflated by five grand.",
    name: "Marcus Rodriguez",
    role: "Sports car Enthusiast",
    initial: "M"
  }
];

const faqs = [
  {
    q: "How accurate is your data?",
    a: "We pull from multiple sources including manufacturer records, service bulletins, and market databases. Our analysis combines public data with pattern recognition to identify issues specific to each model year."
  },
  {
    q: "How do you compare to CarFax?",
    a: "CarFax shows what's been reported. We go deeper by analyzing what those reports mean for your specific vehicle. We uncover common problems, track maintenance gaps, and calculate true market value based on condition."
  },
  {
    q: "Is my information private?",
    a: "Your data stays yours. We don’t sell information or share it with dealers. When you analyze a vehicle, we process your documents securely and delete them after generating your report."
  },
  {
    q: "What if the vehicle has no history?",
    a: "We work with what exists. For vehicles with limited records, we rely on model-specific issue patterns and market comparables. You'll still get our analysis of common problems."
  },
  {
    q: "Can I use this for trade-ins?",
    a: "Yes. Whether you're selling or trading in, our report shows you what a dealer will see. You'll know your vehicle's true market value and can negotiate from solid ground."
  }
];


const HomePage: React.FC = () => {
  // const navigate = useNavigate();

  useEffect(() => {
    // Check for payment status from Stripe redirect
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get('payment_status');
    if (paymentStatus) {
      if (paymentStatus === 'success') {
        message.success('Payment successful! Your credits will be updated shortly.');
      } else if (paymentStatus === 'cancel') {
        message.warning('Payment was cancelled.');
      }
      // Clean the URL to remove the payment_status parameter
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);
  // Default to light for this request, but toggleable
  const [theme, setTheme] = useState("light");

  const toggleTheme = () => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  };

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  return (
    <Layout className="landing-page">
      <TopBar className="title" title="" />

      <Content className="landing-content">

        {/* --- PART 1: TOP CONTENT --- */}
        <div className="top-section-container">

          {/* HERO SECTION */}
          <div className="hero-section">
            <Row className="hero-row">
              <Col xs={24} md={12} className="hero-text-col">
                <Title level={1}>The bullsh*t detector for used cars</Title>
                <Paragraph className="hero-desc">
                  Avoid costly repairs and uncover everything the seller isn't telling you. Common issues, market pricing analysis,
                  photo forensics, missing maintenance, and red flags, generated in minutes. Your first report is <strong>free</strong>.
                </Paragraph>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Space size="middle" style={{ justifyContent: 'center', width: '100%' }}>
                    <div className="hero-button-wrapper">
                      <Link to="/research" className="custom-hero-button-link">
                        <div className="custom-hero-button">
                          <Text strong className="custom-hero-button-title">Research Common Issues</Text>
                        </div>
                      </Link>
                      <Text className="custom-hero-button-subtext">Input a year, make, and model</Text>
                    </div>
                    <div className="hero-button-wrapper">
                      <Link to="/create-report" className="custom-hero-button-link">
                        <div className="custom-hero-button">
                          <Text strong className="custom-hero-button-title">Analyze a Car or Listing</Text>
                        </div>
                      </Link>
                      <Text className="custom-hero-button-subtext">Paste a link or upload your own information</Text>
                    </div>
                  </Space>
                  <Divider className="or-divider">OR</Divider>
                  <a href="https://crdemo.pacer.co/" target="_blank" rel="noopener noreferrer" className="example-link">
                    <Title level={5} style={{ color: 'inherit', margin: 0, textDecoration: 'inherit' }}>See an example</Title>
                  </a>
                </Space>
              </Col>              <Col xs={24} md={12} className="hero-image-col" />
            </Row>
          </div>

          {/* FEATURES SECTION */}
          <div className="features-section">
            <Row justify="space-between" align="top" className="features-header">
              <Col xs={24} md={24}>
                <Title level={3}>Carfax only knows what’s reported. We know what’s actually happening.</Title>
                <Text className="carfax-description">
                  Carfax is great for accidents. But it’s useless for the stuff that actually drains your bank account
                </Text>
              </Col>
            </Row>
            <Row gutter={[{ xs: 24, md: 48 }, { xs: 40, md: 64 }]}>
              <Col xs={24} sm={12} lg={6} className="feature-item">
                <UnorderedListOutlined className="feature-icon" />
                <Title level={5}>Vehicle-specific common problems</Title>
                <Paragraph className="feature-desc">
                  Identify and display vehicle-specific common problems, including their justification and likelihood.
                </Paragraph>
              </Col>
              <Col xs={24} sm={12} lg={6} className="feature-item">
                <ToolOutlined className="feature-icon" />
                <Title level={5}>Interior and exterior damage in photos</Title>
                <Paragraph className="feature-desc">
                  Highlight damages, inconsistencies, and issues found in photos and descriptions.
                </Paragraph>
              </Col>
              <Col xs={24} sm={12} lg={6} className="feature-item">
                <CarOutlined className="feature-icon" />
                <Title level={5}>Missing maintenance and open recalls</Title>
                <Paragraph className="feature-desc">
                  No need to read the CarFax, we automatically flag missing maintenance and anomalies in any vehicle history report.
                </Paragraph>
              </Col>
              <Col xs={24} sm={12} lg={6} className="feature-item">
                <TagOutlined className="feature-icon" />
                <Title level={5}>Market value & what you should pay</Title>
                <Paragraph className="feature-desc">
                  Find out what impacts price and what to pay to get a deal.
                </Paragraph>
              </Col>
            </Row>
          </div>

          <div style={{ textAlign: 'center', marginBottom: '80px' }}>
            <Link to="https://crdemo.pacer.co/" target="_blank" rel="noopener noreferrer">
              <Button size="large" className="features-demo-button">
                See an Example
              </Button>
            </Link>
          </div>

          {/* SELECTION SECTION */}
          <div className="selection-section">
            <Title level={2}>Two ways to get answers</Title>
            <Text className="selection-subtitle">
              Pick your path and get what you need
            </Text>
            <Row gutter={[{ xs: 0, md: 32 }, { xs: 24, md: 32 }]}>
              <Col xs={24} md={12}>
                <div className="choice-card">
                  <div>
                    <SearchOutlined className="card-icon" />
                    <Title level={3}>Search by year, make, and model</Title>
                    <Paragraph>For general research and suggestions.</Paragraph>
                  </div>
                  <div>
                    <Link to="/research">
                      <Button className="btn-card-action" >Analyze</Button>
                    </Link>
                  </div>
                </div>
              </Col>
              <Col xs={24} md={12}>
                <div className="choice-card">
                  <div>
                    <ReadOutlined className="card-icon" />
                    <Title level={3}>Analyze a specific vehicle</Title>
                    <Paragraph>Use a listing URL or upload photos.</Paragraph>
                  </div>
                  <div>
                    <Link to="/create-report">
                      <Button className="btn-card-action">Upload</Button>
                    </Link>
                  </div>
                </div>
              </Col>

            </Row>
          </div>

          {/* --- PART 2: REAL STORIES (Cream BG) --- */}
          <div className="stories-section">
            <div className="container-max">
              <Title level={2}>Real stories</Title>
              <Text className="stories-subtitle">Buyers who found what dealers wouldn't tell them</Text>

              <Row gutter={[24, 24]}>
                {stories.map((story, i) => (
                  <Col xs={24} md={8} key={i}>
                    <div className="story-card">
                      <div>
                        <div className="stars">
                          <StarFilled /> <StarFilled /> <StarFilled /> <StarFilled /> <StarFilled />
                        </div>
                        <Paragraph className="quote">"{story.quote}"</Paragraph>
                      </div>
                      <div className="author-block">
                        {/* <Avatar size="large" style={{ backgroundColor: '#333' }}>{story.initial}</Avatar> */}
                        <div className="author-info">
                          <strong>{story.name}</strong>
                          <span>{story.role}</span>
                        </div>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>

            <div style={{ textAlign: 'center', marginTop: '40px' }}>
              <Link to="https://crdemo.pacer.co/" target="_blank" rel="noopener noreferrer">
                <Button size="large" className="features-demo-button">
                  See an Example
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* --- PART 3: BOTTOM SECTION (Light or Dark based on Theme) --- */}        <div className="bottom-section">
          <div className="container-max">

            {/* FAQ */}
            <div className="faq-section">
              <div className="faq-header">
                <Title level={2}>Questions</Title>
                <Text>Everything you need to know about how Pacer works</Text>
              </div>

              {faqs.map((item, index) => (
                <div className="faq-item" key={index}>
                  <Row gutter={[24, 24]}>
                    <Col xs={24} md={8}>
                      <Title level={4}>{item.q}</Title>
                    </Col>
                    <Col xs={24} md={16}>
                      <Paragraph>{item.a}</Paragraph>
                    </Col>
                  </Row>
                </div>
              ))}

              <div className="need-help">
                <Title level={3}>Need more help?</Title>
                <Text>Reach out and we'll answer what you need to know</Text>
                <br />
                <Button className="btn-contact" onClick={() => window.location.href = 'mailto:info@pacer.co'}>Contact</Button>
              </div>
            </div>

            {/* NEWSLETTER
            <div className="newsletter-section">
              <div className="newsletter-box">
                <Title level={2}>Stay in the loop</Title>
                <Paragraph>Get updates on new features and insights delivered to your inbox</Paragraph>
                <div className="newsletter-form">
                  <Input placeholder="Enter your email" />
                  <Button className="btn-submit">Sign up</Button>
                </div>
                <Text style={{ fontSize: '10px', marginTop: '10px', display: 'block', opacity: 0.6 }}>
                  By signing up you agree to our Terms and Conditions
                </Text>
              </div>
            </div> */}

            {/* FOOTER */}
            <footer className="site-footer">
              <Row gutter={[48, 48]}>
                <Col xs={24} md={6} className="footer-col">
                  <img
                    src={logo} // Conditionally render mobile or full logo
                    alt="Pacer Logo"
                    className="logo"
                  />
                  <Text style={{ display: 'block', marginBottom: '10px' }}>
                    Get the latest updates on vehicle insights and analysis tools.
                  </Text>
                  <div className="subscribe-mini">
                    <Space.Compact style={{ width: '100%' }}>
                      <Input placeholder="Your email" />
                      <Button>Subscribe</Button>
                    </Space.Compact>
                  </div>
                </Col>

                <Col xs={12} md={6} className="footer-links">
                  {/* <Title level={5}>Research</Title>
                  <ul className="coming-soon-list">
                    <li><a href="#">Browse vehicles</a></li>
                    <li><a href="#">Market reports</a></li>
                    <li><a href="#">Common issues</a></li>
                    <li><a href="#">Recall database</a></li>
                    <li><a href="#">Analysis</a></li>
                  </ul> */}
                </Col>

                <Col xs={12} md={6} className="footer-links">
                  {/* <Title level={5}>Analyze a vehicle</Title>
                  <ul>
                    <li><a href="#">Upload documents</a></li>
                    <li><a href="#">View reports</a></li>
                    <li><a href="#">Pricing guide</a></li>
                    <li><a href="#">Follow us</a></li>
                  </ul> */}
                </Col>

                <Col xs={24} md={6} className="footer-links">
                  <Title level={5}>Follow us</Title>
                  <ul>
                    {/* <li><a href="#"><FacebookFilled /> Facebook</a></li> */}
                    {/* <li><a href="#"><InstagramFilled /> Instagram</a></li> */}
                    {/* <li><a href="#"><TwitterOutlined /> X</a></li> */}
                    <li><a href="https://www.linkedin.com/company/pacerco" target="_blank" rel="noreferrer"><LinkedinFilled /> LinkedIn</a></li>
                    {/* <li><a href="#"><YoutubeFilled /> YouTube</a></li> */}
                  </ul>
                </Col>
              </Row>

              <div className="footer-bottom">
                <span>© 2025 Pacer.co All rights reserved.</span>
                <Space size="large">
                  <Link to="/privacy">Privacy Policy</Link>
                  <Link to="/terms">Terms of service</Link>
                  <Link to="/cookies">Cookies settings</Link>
                </Space>
                {/* THEME TOGGLE */}
                <div className="theme-toggle-container">
                  <Space>
                    <Text style={{ fontSize: '12px', fontWeight: 'bold' }}>
                      {theme === 'dark' ? 'Dark Mode' : 'Light Mode'}
                    </Text>
                    <Switch
                      checked={theme === "dark"}
                      onChange={toggleTheme}
                      checkedChildren={<BulbFilled />}
                      unCheckedChildren={<BulbOutlined />}
                      style={{ backgroundColor: theme === 'dark' ? '#dfff00' : '#ccc' }}
                    />
                  </Space>
                </div>
              </div>
            </footer>

          </div>
        </div>

      </Content>
    </Layout>
  );
};

export default HomePage;